import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { teacherModuleAPI } from '../../services/api';
import { invalidateTeacherCalendar } from '../../utils/invalidateTeacherCalendar';
import SubstituteTeacherPicker, { parseSubstituteTeachersResponse } from './SubstituteTeacherPicker';
import MergeBatchPicker from './MergeBatchPicker';

export default function SessionCoverDialog({
  open,
  onClose,
  sessionId,
  batchName,
  currentSubstituteId = '',
}) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState('substitute');
  const [substituteId, setSubstituteId] = useState('');
  const [mergeTargetId, setMergeTargetId] = useState('');

  useEffect(() => {
    if (!open) return;
    setSubstituteId(currentSubstituteId ? String(currentSubstituteId) : '');
    setMode('substitute');
    setMergeTargetId('');
  }, [open, currentSubstituteId, sessionId]);

  const substitutesQuery = useQuery(
    ['sessionSubstitutes', sessionId],
    async () => {
      const res = await teacherModuleAPI.getSessionAvailableSubstitutes(sessionId);
      return parseSubstituteTeachersResponse(res);
    },
    { enabled: open && !!sessionId && mode === 'substitute' }
  );

  const assignMutation = useMutation(
    () => teacherModuleAPI.assignSessionSubstitute(sessionId, { substituteTeacherId: substituteId }),
    {
      onSuccess: () => {
        toast.success('Substitute assigned for this session');
        queryClient.invalidateQueries(['teacherSessionDetail', sessionId]);
        invalidateTeacherCalendar(queryClient);
        onClose();
      },
      onError: (e) => toast.error(e.response?.data?.message || 'Failed to assign substitute'),
    }
  );

  const mergeMutation = useMutation(
    () => teacherModuleAPI.mergeSession(sessionId, { targetSessionId: mergeTargetId }),
    {
      onSuccess: (res) => {
        toast.success(
          res.data?.data?.keptOriginalTime
            ? res.data.message
            : 'Session merged'
        );
        queryClient.invalidateQueries(['teacherSessionDetail', sessionId]);
        queryClient.invalidateQueries(['batch']);
        invalidateTeacherCalendar(queryClient);
        onClose();
      },
      onError: (e) => toast.error(e.response?.data?.message || 'Failed to merge session'),
    }
  );

  const { available: teachers, unavailable, recentSubstitutions = [] } = substitutesQuery.data || {
    available: [],
    unavailable: [],
    recentSubstitutions: [],
  };
  const busy = assignMutation.isLoading || mergeMutation.isLoading;
  const canSubmit =
    mode === 'substitute' ? !!substituteId : !!mergeTargetId;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Session coverage — {batchName || 'Class'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {currentSubstituteId
            ? 'Change the substitute for this class, or merge students into another batch. The primary teacher stays in the list and is marked when they are on leave.'
            : 'Assign a substitute teacher for this session only, or merge students into another batch — including a class at a different time. The primary teacher stays in the list and is marked when they are on leave.'}
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_, v) => v && setMode(v)}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="substitute">
            {currentSubstituteId ? 'Change substitute' : 'Assign substitute'}
          </ToggleButton>
          <ToggleButton value="merge">Merge batch</ToggleButton>
        </ToggleButtonGroup>

        {mode === 'substitute' ? (
          <SubstituteTeacherPicker
            available={teachers}
            unavailable={unavailable}
            loading={substitutesQuery.isLoading}
            value={substituteId}
            onChange={setSubstituteId}
            recentSubstitutions={recentSubstitutions}
            emptyMessage="No available substitute teachers for this time slot."
          />
        ) : null}

        {mode === 'merge' ? (
          <MergeBatchPicker sessionId={sessionId} value={mergeTargetId} onChange={setMergeTargetId} />
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={busy || !canSubmit}
          onClick={() => (mode === 'substitute' ? assignMutation.mutate() : mergeMutation.mutate())}
        >
          {busy
            ? 'Saving…'
            : mode === 'substitute'
              ? currentSubstituteId
                ? 'Change substitute'
                : 'Assign substitute'
              : 'Merge'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
